// Container editing only: compressed image data is never decoded or re-encoded.
const text = (bytes: Uint8Array) => new TextDecoder('latin1').decode(bytes);
const ascii = (value: string) => new TextEncoder().encode(value);
const join = (parts: Uint8Array[]) => {
  const result = new Uint8Array(
    parts.reduce((size, part) => size + part.length, 0)
  );
  let offset = 0;
  for (const part of parts) {
    result.set(part, offset);
    offset += part.length;
  }
  return result;
};
const requireValid = (condition: boolean) => {
  if (!condition) throw new Error('Invalid image metadata structure');
};

// Allow only fixed-size technical values. Never copy arbitrary strings, MakerNote,
// GPS/Interop pointers, thumbnails, serial numbers, timestamps or unused TIFF bytes.
const primaryTags = new Map<number, number[]>([
  [0x100, [3, 4]],
  [0x101, [3, 4]],
  [0x112, [3]],
  [0x11a, [5]],
  [0x11b, [5]],
  [0x128, [3]],
  [0x213, [3]],
]);
const exposureTags = new Map<number, number[]>([
  [0x829a, [5]],
  [0x829d, [5]],
  [0x8822, [3]],
  [0x8827, [3]],
  [0x9201, [10]],
  [0x9202, [5]],
  [0x9203, [10]],
  [0x9204, [10]],
  [0x9205, [5]],
  [0x9207, [3]],
  [0x9208, [3]],
  [0x9209, [3]],
  [0x920a, [5]],
  [0xa001, [3]],
  [0xa002, [3, 4]],
  [0xa003, [3, 4]],
  [0xa20e, [5]],
  [0xa20f, [5]],
  [0xa210, [3]],
  [0xa217, [3]],
  [0xa401, [3]],
  [0xa402, [3]],
  [0xa403, [3]],
  [0xa404, [5]],
  [0xa405, [3]],
  [0xa406, [3]],
  [0xa407, [3]],
  [0xa408, [3]],
  [0xa409, [3]],
  [0xa40a, [3]],
]);

type Entry = { tag: number; type: number; value: Uint8Array };
function cleanExif(input: Uint8Array): Uint8Array {
  const prefixed = text(input.subarray(0, 6)) === 'Exif\0\0';
  const bytes = input.subarray(prefixed ? 6 : 0);
  requireValid(bytes.length >= 8);
  const order = text(bytes.subarray(0, 2));
  requireValid(order === 'II' || order === 'MM');
  const little = order === 'II';
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  requireValid(view.getUint16(2, little) === 42);
  let exifOffset = 0;
  function readIFD(
    offset: number,
    allowed: Map<number, number[]>,
    root: boolean
  ): Entry[] {
    requireValid(offset >= 8 && offset + 2 <= bytes.length);
    const count = view.getUint16(offset, little);
    requireValid(offset + 2 + count * 12 + 4 <= bytes.length);
    const entries: Entry[] = [];
    const seen = new Set<number>();
    for (let i = 0; i < count; i++) {
      const pos = offset + 2 + i * 12;
      const tag = view.getUint16(pos, little);
      const type = view.getUint16(pos + 2, little);
      const length = view.getUint32(pos + 4, little);
      if (root && tag === 0x8769) {
        requireValid(type === 4 && length === 1 && exifOffset === 0);
        exifOffset = view.getUint32(pos + 8, little);
      }
      if (!allowed.get(tag)?.includes(type)) continue;
      // ISO sensitivity permits multiple values. Omit that optional setting
      // rather than rejecting an otherwise cleanable image.
      if (tag === 0x8827 && length !== 1) continue;
      requireValid(length === 1 && !seen.has(tag));
      seen.add(tag);
      const size = type === 3 ? 2 : type === 4 ? 4 : 8;
      const start = size <= 4 ? pos + 8 : view.getUint32(pos + 8, little);
      requireValid(start >= 8 && start + size <= bytes.length);
      if (tag === 0x112)
        requireValid(
          view.getUint16(start, little) >= 1 &&
            view.getUint16(start, little) <= 8
        );
      entries.push({ tag, type, value: bytes.slice(start, start + size) });
    }
    return entries.sort((a, b) => a.tag - b.tag);
  }
  const primary = readIFD(view.getUint32(4, little), primaryTags, true);
  const exposure = exifOffset ? readIFD(exifOffset, exposureTags, false) : [];
  const secondaryOffset =
    8 + 2 + (primary.length + (exposure.length ? 1 : 0)) * 12 + 4;
  if (exposure.length) {
    const pointer = new Uint8Array(4);
    new DataView(pointer.buffer).setUint32(0, secondaryOffset, little);
    primary.push({ tag: 0x8769, type: 4, value: pointer });
  }
  const dataOffset =
    secondaryOffset + (exposure.length ? 2 + exposure.length * 12 + 4 : 0);
  const result = new Uint8Array(
    dataOffset +
      [...primary, ...exposure].reduce(
        (n, e) => n + (e.value.length > 4 ? e.value.length : 0),
        0
      )
  );
  const out = new DataView(result.buffer);
  result.set(ascii(order));
  out.setUint16(2, 42, little);
  out.setUint32(4, 8, little);
  let cursor = dataOffset;
  function writeIFD(entries: Entry[], offset: number) {
    out.setUint16(offset, entries.length, little);
    entries.forEach((entry, index) => {
      const pos = offset + 2 + index * 12;
      out.setUint16(pos, entry.tag, little);
      out.setUint16(pos + 2, entry.type, little);
      out.setUint32(pos + 4, 1, little);
      if (entry.value.length <= 4) result.set(entry.value, pos + 8);
      else {
        out.setUint32(pos + 8, cursor, little);
        result.set(entry.value, cursor);
        cursor += entry.value.length;
      }
    });
  }
  writeIFD(primary, 8);
  if (exposure.length) writeIFD(exposure, secondaryOffset);
  return prefixed ? join([ascii('Exif\0\0'), result]) : result;
}

function jpegSegment(marker: number, payload: Uint8Array) {
  requireValid(payload.length <= 65533);
  return join([
    new Uint8Array([
      255,
      marker,
      (payload.length + 2) >> 8,
      (payload.length + 2) & 255,
    ]),
    payload,
  ]);
}
function cleanJpeg(bytes: Uint8Array) {
  const parts = [bytes.subarray(0, 2)];
  let pos = 2;
  let scanned = false;
  while (pos < bytes.length) {
    const start = pos;
    requireValid(bytes[pos++] === 255);
    while (bytes[pos] === 255) pos++;
    const marker = bytes[pos++];
    requireValid(marker !== undefined && marker !== 0 && marker !== 0xd8);
    if (marker === 0xd9) {
      requireValid(scanned);
      parts.push(bytes.subarray(start, pos));
      return join(parts); // Discard trailing data outside the image container.
    }
    requireValid(pos + 2 <= bytes.length);
    const size = (bytes[pos]! << 8) | bytes[pos + 1]!;
    const end = pos + size;
    requireValid(size >= 2 && end <= bytes.length);
    const payload = bytes.subarray(pos + 2, end);
    if (marker === 0xe1 && text(payload.subarray(0, 6)) === 'Exif\0\0') {
      parts.push(jpegSegment(marker, cleanExif(payload)));
    } else if (marker === 0xe0 && text(payload.subarray(0, 5)) === 'JFIF\0') {
      requireValid(payload.length >= 14);
      const header = payload.slice(0, 14);
      header[12] = 0;
      header[13] = 0;
      parts.push(jpegSegment(marker, header)); // Remove embedded JFIF thumbnail.
    } else if (
      (marker === 0xe2 && text(payload.subarray(0, 12)) === 'ICC_PROFILE\0') ||
      (marker === 0xee && text(payload.subarray(0, 5)) === 'Adobe') ||
      (marker !== 0xfe && !(marker! >= 0xe0 && marker! <= 0xef))
    ) {
      parts.push(bytes.subarray(start, end));
    }
    pos = end;
    if (marker === 0xda) {
      scanned = true;
      const scanStart = pos;
      while (pos < bytes.length) {
        if (bytes[pos] !== 255) {
          pos++;
          continue;
        }
        let next = pos + 1;
        while (bytes[next] === 255) next++;
        if (
          bytes[next] === 0 ||
          (bytes[next]! >= 0xd0 && bytes[next]! <= 0xd7)
        ) {
          pos = next + 1;
          continue;
        }
        break;
      }
      parts.push(bytes.subarray(scanStart, pos));
    }
  }
  throw new Error('Missing JPEG end marker');
}

function pngChunk(name: string, payload: Uint8Array) {
  const chunk = new Uint8Array(payload.length + 12);
  const view = new DataView(chunk.buffer);
  view.setUint32(0, payload.length);
  chunk.set(ascii(name), 4);
  chunk.set(payload, 8);
  let crc = 0xffffffff;
  for (const byte of chunk.subarray(4, chunk.length - 4)) {
    crc ^= byte;
    for (let i = 0; i < 8; i++) crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
  }
  view.setUint32(chunk.length - 4, (crc ^ 0xffffffff) >>> 0);
  return chunk;
}
const pngTechnical = new Set([
  'IHDR',
  'PLTE',
  'IDAT',
  'IEND',
  'cHRM',
  'gAMA',
  'iCCP',
  'sBIT',
  'sRGB',
  'bKGD',
  'hIST',
  'tRNS',
  'pHYs',
  'acTL',
  'fcTL',
  'fdAT',
  'cICP',
  'mDCV',
  'cLLI',
]);
function cleanPng(bytes: Uint8Array) {
  const parts = [bytes.subarray(0, 8)];
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let pos = 8;
  let hasData = false;
  while (pos + 12 <= bytes.length) {
    const size = view.getUint32(pos);
    const end = pos + 12 + size;
    requireValid(end <= bytes.length);
    const name = text(bytes.subarray(pos + 4, pos + 8));
    requireValid(pos !== 8 || (name === 'IHDR' && size === 13));
    if (name === 'eXIf')
      parts.push(pngChunk(name, cleanExif(bytes.subarray(pos + 8, end - 4))));
    else if (pngTechnical.has(name)) parts.push(bytes.subarray(pos, end));
    else requireValid((bytes[pos + 4]! & 32) !== 0); // Unknown critical chunks cannot be safely edited.
    if (name === 'IDAT') hasData = true;
    if (name === 'IEND') {
      requireValid(size === 0 && hasData);
      return join(parts);
    }
    pos = end;
  }
  throw new Error('Missing PNG end chunk');
}

function webpChunk(name: string, payload: Uint8Array) {
  const result = new Uint8Array(8 + payload.length + (payload.length % 2));
  result.set(ascii(name));
  new DataView(result.buffer).setUint32(4, payload.length, true);
  result.set(payload, 8);
  return result;
}
// ANMF embeds its own RIFF subchunks. Preserve the frame header and encoded
// alpha/pixels, but do not allow opaque private subchunks to bypass cleaning.
function cleanWebpFrame(bytes: Uint8Array): Uint8Array {
  requireValid(bytes.length >= 16);
  const parts = [bytes.subarray(0, 16)];
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let pos = 16;
  let hasImage = false;
  while (pos + 8 <= bytes.length) {
    const name = text(bytes.subarray(pos, pos + 4));
    const size = view.getUint32(pos + 4, true);
    const end = pos + 8 + size + (size % 2);
    requireValid(end <= bytes.length);
    if (['ALPH', 'VP8 ', 'VP8L'].includes(name))
      parts.push(bytes.subarray(pos, end));
    if (name === 'VP8 ' || name === 'VP8L') hasImage = true;
    pos = end;
  }
  requireValid(pos === bytes.length && hasImage);
  return join(parts);
}
const webpTechnical = new Set(['ICCP', 'ANIM', 'ANMF', 'ALPH', 'VP8 ', 'VP8L']);
function cleanWebp(bytes: Uint8Array) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const limit = view.getUint32(4, true) + 8;
  requireValid(limit >= 12 && limit <= bytes.length);
  const parts: Uint8Array[] = [];
  let pos = 12;
  let extended: Uint8Array | undefined;
  let hasExif = false;
  let hasImage = false;
  while (pos + 8 <= limit) {
    const name = text(bytes.subarray(pos, pos + 4));
    const size = view.getUint32(pos + 4, true);
    const end = pos + 8 + size + (size % 2);
    requireValid(end <= limit);
    if (name === 'EXIF') {
      parts.push(
        webpChunk(name, cleanExif(bytes.subarray(pos + 8, pos + 8 + size)))
      );
      hasExif = true;
    } else if (name === 'VP8X') {
      requireValid(size === 10 && !extended);
      extended = bytes.slice(pos, end);
      parts.push(extended);
    } else if (name === 'ANMF') {
      parts.push(
        webpChunk(name, cleanWebpFrame(bytes.subarray(pos + 8, pos + 8 + size)))
      );
    } else if (webpTechnical.has(name)) parts.push(bytes.subarray(pos, end));
    if (['VP8 ', 'VP8L', 'ANMF'].includes(name)) hasImage = true;
    pos = end;
  }
  requireValid(pos === limit && hasImage && (!hasExif || !!extended));
  if (extended) extended[8] = (extended[8]! & ~0x0c) | (hasExif ? 0x08 : 0);
  const body = join(parts);
  const header = bytes.slice(0, 12);
  new DataView(header.buffer).setUint32(4, body.length + 4, true);
  return join([header, body]);
}

export function sanitizeImageMetadata(
  bytes: Uint8Array,
  type: string
): Uint8Array {
  if (type === 'image/jpeg') return cleanJpeg(bytes);
  if (type === 'image/png') return cleanPng(bytes);
  if (type === 'image/webp') return cleanWebp(bytes);
  throw new Error('Unsupported image type');
}
